<template>
  <q-card v-if="reviewAnalysis && reviewAnalysis.count_reviews" flat>
    <q-card-section>
      <div class="modal-title q-mb-xs">Review Analysis</div>
      <div class="text-grey-7">
        Reviews pulled from internet listing services over the past two years. Total of
        <b>{{ formatNumber(reviewAnalysis.count_reviews) }}</b>
        public reviews for this property.
      </div>
    </q-card-section>
    <q-separator class="q-mb-sm" />
    <div v-if="bars.length > 0">
      <div
        v-for="(
          {
            category,
            label,
            explanation,
            countNegative,
            countPositive,
            totalCount,
            percPositive,
            percNegative
          },
          idx
        ) in bars"
        :key="category"
      >
        <div
          :class="{ 'q-mb-sm': idx === bars.length - 1 }"
          class="row q-pa-md items-center q-col-gutter-sm no-wrap"
        >
          <div class="column items-center q-px-md">
            <div class="half-gauge">
              <q-circular-progress
                show-value
                :min="0"
                :max="200"
                :class="percPositive >= percNegative ? 'text-green-9' : 'text-red-8'"
                :value="percPositive * 100.0"
                font-size="14px"
                size="80px"
                color="green"
                :angle="-90"
                track-color="red"
              >
                <q-icon v-if="percPositive >= percNegative" name="thumb_up" />
                <q-icon v-else name="thumb_down" />
              </q-circular-progress>
            </div>
            <div class="subtitle col-2 q-mt-xs">{{ totalCount }} Mentions</div>
            <q-tooltip anchor="top middle" self="bottom middle">
              <div class="q-pa-xs">
                <div class="text-caption">{{ formatPercent(percPositive, 1) }} Positive</div>
                <div class="q-mb-sm">{{ countPositive }} Mentions</div>
                <div class="text-caption">{{ formatPercent(percNegative, 1) }} Negative</div>
                <div>{{ countNegative }} Mentions</div>
              </div>
            </q-tooltip>
          </div>
          <div class="column">
            <div class="title">{{ label }}</div>
            <div class="subtitle">{{ explanation }}</div>
          </div>
        </div>
        <q-separator v-if="idx < bars.length - 1" />
      </div>
    </div>
    <div v-else>Not enough reviews to show analysis</div>
  </q-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { ReviewAnalysis } from 'common'
import { formatNumber, formatPercent } from '@/utils'

export default {
  name: 'CardReviewAnalysis',
  props: {
    reviewAnalysis: {
      type: Object as PropType<ReviewAnalysis>,
      required: true
    }
  },
  data() {
    return {
      formatPercent,
      formatNumber,
      categories: {
        location: {
          label: 'Location',
          explanation:
            'Proximity to amenities (shops, restaurants, schools), accessibility (public transport, highways), neighborhood safety'
        },
        amenities_and_features: {
          label: 'Amenities and Features',
          explanation:
            'Quality and availability of amenities (gym, pool, parking), apartment features (size, floor plan, storage), noise levels (soundproofing, neighborhood noise)'
        },
        staff_and_management: {
          label: 'Staff and Management',
          explanation:
            'Responsiveness of maintenance requests, professionalism and helpfulness of staff, overall management quality'
        },
        cleanliness_and_maintenance: {
          label: 'Cleanliness and Maintenance',
          explanation: 'Cleanliness, pest control, maintenance quality'
        },
        value: {
          label: 'Value',
          explanation:
            'Rent affordability, comparison with nearby properties, cost of additional services (utilities, laundry)'
        }
      } as Record<string, { label: string; explanation: string }>
    }
  },
  computed: {
    bars(): {
      category: string
      label: string
      explanation: string
      countPositive: number
      countNegative: number
      percPositive: number
      percNegative: number
      totalCount: number
    }[] {
      const results = []
      const threshold = 0.05 // only show categories with at least 5% of reviews
      // iterate over keys in this.categories, and compute the percentage of positive and negative reviews
      for (const [category, { label }] of Object.entries(this.categories)) {
        const positiveCount = this.reviewAnalysis.positive_counts[category] || 0
        const negativeCount = this.reviewAnalysis.negative_counts[category] || 0
        const totalCount = positiveCount + negativeCount
        if (
          this.reviewAnalysis.count_reviews &&
          (totalCount / this.reviewAnalysis.count_reviews > threshold || totalCount > 10)
        ) {
          results.push({
            category,
            label,
            explanation: this.categories[category].explanation,
            countPositive: positiveCount,
            countNegative: negativeCount,
            percPositive: totalCount ? positiveCount / totalCount : 0,
            percNegative: totalCount ? negativeCount / totalCount : 0,
            totalCount
          })
        }
      }

      return results.sort((a, b) => b.percPositive - a.percPositive)
    }
  },
  mounted() {
    this.$tracker.openedPopup('review_analysis')
  }
}
</script>

<style scoped lang="scss">
.title {
  font-size: 14px;
  font-weight: medium;
}
.subtitle {
  font-size: 12px;
  color: $grey-8;
}
.half-gauge {
  clip-path: inset(-100% -100% 0 -100%);
  overflow: hidden;
}
.half-gauge > * {
  margin-bottom: -51%;
}
:deep(.q-circular-progress__text) {
  text-align: center;
  top: 35%;
  transform: translate3d(0, -50%, 0);
  flex-direction: column;
}
.modal-title {
  font-size: 20px;
  font-weight: 400;
}
</style>
